import React, { useLayoutEffect, useRef } from "react";
import { numberFormatter } from "@common/helpers/number";
import { currencyFormatter } from "@common/helpers/currency";
import { NumberFormat } from "@design-system/NumberFormat/NumberFormat";
import styles from "../SpreadsheetGrid.module.scss";
import { BaseCellProps } from ".";

interface NumberCellProps extends BaseCellProps<number> {
    isMonetary?: boolean;
    allowNegative?: boolean;
}

export const NumberCell: React.FC<NumberCellProps> = ({
    value,
    onChange,
    isEditing,
    onBlur,
    isMonetary,
    allowNegative = true,
}) => {
    const inputContainerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        if (!isEditing || !inputContainerRef.current) {
            return;
        }
        const input = inputContainerRef.current.querySelector("input");
        // Spreadsheet semantics: editing starts with the whole value selected
        // so the first keystroke replaces it instead of appending. Selecting
        // synchronously here covers type-to-edit (the initiating keystroke's
        // native insertion fires before react-number-format's deferred caret
        // handling); the selectOnFocus/onFocus handlers below cover the
        // pointer path, where that deferred handling would undo this one.
        input?.focus();
        input?.select();
    }, [isEditing]);

    if (isEditing) {
        return (
            <div
                ref={inputContainerRef}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <NumberFormat
                    className={styles.input}
                    value={typeof value === "number" ? value : undefined}
                    onValueChange={(values) =>
                        onChange(values.floatValue ?? null)
                    }
                    onBlur={onBlur}
                    onFocus={(event) => event.currentTarget.select()}
                    thousandSeparator
                    allowNegative={allowNegative}
                    prefix={isMonetary ? "$ " : undefined}
                    decimalScale={isMonetary ? 2 : undefined}
                    fixedDecimalScale={isMonetary && typeof value === "number"}
                />
            </div>
        );
    }

    if (value === null || value === undefined) {
        return null;
    }

    if (isMonetary) {
        return currencyFormatter.format(value);
    }

    return numberFormatter.format(value);
};
